import type React from 'react';

const CHUNK_RECOVERY_KEY = 'ai_marketing_chunk_recovery_attempted';

const getChunkRecoveryKey = () => {
  const mainScript = Array.from(document.scripts)
    .map((script) => script.src)
    .find((src) => /\/static\/js\/main\.[^/]+\.js/.test(src));

  return mainScript ? `${CHUNK_RECOVERY_KEY}:${mainScript}` : CHUNK_RECOVERY_KEY;
};

export const isChunkLoadFailure = (error: unknown): boolean => {
  const value = error instanceof Error ? `${error.name} ${error.message}` : String(error || '');
  return (
    value.includes('ChunkLoadError') ||
    value.includes('Loading chunk') ||
    value.includes('Loading CSS chunk') ||
    (value.includes('Unexpected token') && value.includes('<'))
  );
};

const clearRuntimeCaches = async () => {
  if ('caches' in window) {
    try {
      const names = await window.caches.keys();
      await Promise.all(names.map((name) => window.caches.delete(name)));
    } catch {
      // Cache cleanup is best-effort only.
    }
  }
};

export const recoverFromChunkLoadError = async () => {
  const recoveryKey = getChunkRecoveryKey();

  if (sessionStorage.getItem(recoveryKey) === '1') {
    return;
  }

  sessionStorage.setItem(recoveryKey, '1');
  await clearRuntimeCaches();
  window.location.reload();
};

export const lazyWithChunkRecovery = <T extends { default: React.ComponentType<any> }>(
  loader: () => Promise<T>
): Promise<T> =>
  loader().catch(async (error) => {
    if (isChunkLoadFailure(error)) {
      await recoverFromChunkLoadError();
    }
    throw error;
  });

export const registerChunkRecovery = () => {
  window.addEventListener('error', (event) => {
    if (isChunkLoadFailure(event.error || event.message)) {
      recoverFromChunkLoadError();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadFailure(event.reason)) {
      recoverFromChunkLoadError();
    }
  });
};

export const resetChunkRecoveryFlag = () => {
  Object.keys(sessionStorage)
    .filter((key) => key === CHUNK_RECOVERY_KEY || key.startsWith(`${CHUNK_RECOVERY_KEY}:`))
    .forEach((key) => sessionStorage.removeItem(key));
};
